import React, { useState } from 'react';
import {
  Button,
  Drawer,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
// Styles
import { Close } from '@mui/icons-material';
import { ErrorMessage, Field, FieldProps, Form, Formik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { checkExistingEmail, createUser, fetchUserList } from './actions';
import { Watch } from 'react-loader-spinner';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import __ from 'lodash';
import { setErrorToastState } from 'Store/action';
import { set_create_user_drawer } from 'Store/reducers/adminReducer';
import { set_users } from 'Store/reducers/userReducer';
import { AppDispatch, RootState } from 'Store/Store';
import { EMAIL_REGEX } from 'shared/constants/regex';

export const CreateUser: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const create_user_drawer = useSelector((state: RootState) => state?.admin?.create_user_drawer);
  const [emailUnique, setEmailUnique] = useState([]);
  const [loading, setLoading] = useState(false);

  const initialValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    role: '',
  };

  const RequestSchema = Yup.object().shape({
    first_name: Yup.string().max(300).required('Required'),
    last_name: Yup.string().max(300).required('Required'),
    email: Yup.string().max(300).matches(EMAIL_REGEX, 'Invalid email format').email('Invalid email').required('Required'),
    phone: Yup.string().max(255),
    password: Yup.string()
      .matches(
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/,
        'Password must contain at least 8 characters, one uppercase, one number and one special case character',
      )
      .required('Required!'),
    role: Yup.string().min(1).required('Required!'),
  });

  const handleSubmit = (data: any) => {
    setLoading(true);
    createUser(data)
      .then(res => {
        if (res.status === 200) {
          fetchUserList().then(res => {
            if (res.status === 200) {
              dispatch(set_users(res.data));
            }
          });
          dispatch(set_create_user_drawer(false));
        }
        setLoading(false);
      })
      .catch(err => {
        dispatch(setErrorToastState(true, 'Something went wrong, please try again in a while.'));
        setLoading(false);
      });
  };

  const inputFilter = __.debounce(email => {
    setEmailUnique([]);
    if (email) {
      checkExistingEmail(email)
        .then(res => {
          setEmailUnique(res.data.data);
        })
        .catch(err => {
          dispatch(setErrorToastState(true, 'Something went wrong, please try again in a while.'));
        });
    }
  }, 300);

  return (
    <Drawer
      BackdropProps={{ invisible: true }}
      anchor="right"
      open={create_user_drawer}
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}>
      <Formik
        validationSchema={RequestSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={initialValues}>
        {({ values, touched, errors, handleSubmit }) => (
          <Form style={{ height: '100%' }}>
            <Grid
              container
              flexWrap="nowrap"
              direction="column"
              justifyContent="space-between"
              spacing={4}>
              <Grid container direction="column" spacing={4}>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                  <Grid>
                    <Typography variant="h4">Create new user</Typography>
                  </Grid>
                  <Grid>
                    <IconButton
                      disabled={loading}
                      onClick={() => dispatch(set_create_user_drawer(false))}>
                      <Close />
                    </IconButton>
                  </Grid>
                </Grid>
                <Grid>
                  <Typography variant="h6">User account</Typography>
                </Grid>
                <Grid container direction="column" spacing={1}>
                  <Grid container>
                    <Grid
                      size={{
                        md: 'grow',
                      }}
                      container
                      direction="column"
                      spacing={1}>
                      <Grid>
                        <Typography variant="h5">First name</Typography>
                      </Grid>
                      <Grid>
                        <Field name="first_name">
                          {({ field }: FieldProps) => (
                            <TextField
                              variant="standard"
                              {...field}
                              fullWidth
                              placeholder="First Name"
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid>
                        <ErrorMessage name="first_name">
                          {message => <Typography color="error">{message}</Typography>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                    <Grid
                      size={{
                        md: 'grow',
                      }}
                      container
                      spacing={1}
                      direction="column">
                      <Grid>
                        <Typography variant="h5">Last name</Typography>
                      </Grid>
                      <Grid>
                        <Field name="last_name">
                          {({ field }: FieldProps) => (
                            <TextField
                              variant="standard"
                              {...field}
                              fullWidth
                              placeholder="Last Name"
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid>
                        <ErrorMessage name="last_name">
                          {message => <Typography color="error">{message}</Typography>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5">Email</Typography>
                    </Grid>
                    <Grid>
                      <Field name="email">
                        {({ field }: FieldProps) => (
                          <TextField
                            variant="standard"
                            {...field}
                            fullWidth
                            onBlur={event => {
                              inputFilter(event.target.value);
                            }}
                            placeholder="name.surname@example.com"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center">
                      <Grid>
                        <ErrorMessage name="email">
                          {message => <Typography color="error">{message}</Typography>}
                        </ErrorMessage>
                      </Grid>
                      <Grid>
                        {emailUnique.length > 0 ? (
                          <Typography style={{ color: 'red' }}>Email already taken</Typography>
                        ) : null}
                      </Grid>
                    </Grid>                    
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5">Phone Number</Typography>
                    </Grid>
                    <Grid>
                      <Field name="phone">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            placeholder="+44 (0) 7123 456 789"
                            {...field}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="phone">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5">Password</Typography>
                    </Grid>
                    <Grid>
                      <Field name="password">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            {...field}
                            variant="standard"
                            type="password"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="password">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid>
                  <Divider color="#F1F1F1" />
                </Grid>
                <Grid>
                  <Typography variant="h6">User account</Typography>
                </Grid>

                <Grid container direction="column" spacing={1}>
                  <Grid>
                    <Typography variant="h5">Role</Typography>
                  </Grid>
                  <Grid>
                    <Field name="role">
                      {({ field }: FieldProps) => (
                        <Select
                          disableUnderline
                          {...field}
                          fullWidth
                          value={field.value}
                          variant="standard"
                          error={touched.role && !!errors.role ? true : false}>
                          <MenuItem value="" selected disabled>
                            Select Role
                          </MenuItem>
                          <MenuItem value="super_admin">Super Admin</MenuItem>
                          <MenuItem value="client">Client</MenuItem>
                        </Select>
                      )}
                    </Field>
                  </Grid>
                  <Grid>
                    <ErrorMessage name="role">
                      {message => <Typography color="error">{message}</Typography>}
                    </ErrorMessage>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container direction="column" spacing={2} mb={4}>
                <Grid>
                  <LoadingButton
                    onClick={() => handleSubmit()}
                    loadingIndicator={<Watch color="#F1F1F1" width={30} height={20} />}
                    loading={loading}
                    variant="contained"
                    color="secondary"
                    fullWidth>
                    Create User
                  </LoadingButton>
                </Grid>
                <Grid>
                  <Button
                    variant="outlinedWhite"
                    disabled={loading}
                    fullWidth
                    onClick={() => dispatch(set_create_user_drawer(false))}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default CreateUser;
